import React, { useCallback, useEffect, useMemo, useState } from "react";
import invert from "lodash/invert";
import styled from "styled-components/macro";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";

import AppBar from "@material-ui/core/AppBar";
import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import Divider from "@material-ui/core/Divider";
import Hidden from "@material-ui/core/Hidden";
import MuiTab from "@material-ui/core/Tab";
import MuiToolbar from "@material-ui/core/Toolbar";
import Tabs from "@material-ui/core/Tabs";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import { withStyles, useTheme } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import SettingsIcon from "@material-ui/icons/Settings";
import SpaIcon from "@material-ui/icons/Spa";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import CastForEducationIcon from "@material-ui/icons/CastForEducation";
import DeviceHubIcon from "@material-ui/icons/DeviceHub";
import SecurityIcon from "@material-ui/icons/Security";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import ForumIcon from "@material-ui/icons/Forum";
import Group from "@material-ui/icons/Group";
import SignOutIcon from "../icons/SignOutIcon";
//import CollectionsIcon from "@material-ui/icons/Collections";
import PermMediaIcon from "@material-ui/icons/PermMedia";
import DevicesIcon from "@material-ui/icons/Devices";
import { useAppContext } from "../api/AppContext";
import SemanticsSearch from "./Journey/SemanticsSearch";
import WelfareSearch from "./Welfares/WelfareSearch";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import CodeIcon from "@material-ui/icons/Code";
import {
  isCyberTipsSection,
  getCyberTipsEntityLabel,
} from "./Copilot/cyberTipsSections";
import {
  ANGELINI_COMPANY_ID,
  CDP_COMPANY_ID,
  DIGITALATTITUDE_COMPANY_ID,
  SNAM_COMPANY_ID,
  isCdpOnlyUser,
} from "../lib/companyAccess";

const ENIKNOWLEDGEHUB_COMPANY_ID = "f4a3cc35-ad56-47dc-92db-840e6ec08cfb";
const ALLITUDE_COMPANY_ID = "21330221-0072-45a1-88e1-d6ad6de3bf40";

const SignOutButton = withStyles({
  root: {
    fontSize: "0.875rem",
    fontWeight: "normal",
    whiteSpace: "nowrap",
  },
})(Button);

const ProfileAvatar = withStyles({
  root: {
    backgroundColor: "#F2E7FE",
    backgroundImage: "url(/avatar-background.png)",
    backgroundSize: "cover",
    color: "#681CE3",
  },
})(Avatar);

const Tab = withStyles({
  root: {
    color: "#212121",
    fontFamily: "Roboto",
    fontSize: "14px",
    letterSpacing: "unset",
    lineHeight: "16px",
    minWidth: "fit-content",
    textTransform: "none",
    whiteSpace: "nowrap",
  },
})(MuiTab);

const SettingsTab = withStyles({
  root: {
    color: "#212121",
    fontFamily: "Roboto",
    fontSize: "12px",
    letterSpacing: "unset",
    lineHeight: "14px",
    minWidth: "auto",
    maxWidth: 120,
    padding: "6px 8px",
    textTransform: "none",
    whiteSpace: "normal",
  },
  wrapper: {
    padding: 0,
  },
})(MuiTab);

const SettingsTabs = withStyles({
  root: {
    maxWidth: "100%",
    minHeight: 40,
  },
  flexContainer: {
    height: 40,
  },
  scrollButtons: {
    width: 24,
  },
})(Tabs);

const Toolbar = withStyles(({ spacing }) => ({
  root: {
    minHeight: 48,
    paddingLeft: spacing(7.5),
    paddingRight: spacing(7.5),
  },
}))(MuiToolbar);

const ToolbarContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-grow: 1;
  align-items: center;
  width: 100%;
  > * {
    &:not(:last-child) {
      margin-right: 10px;
    }
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  > *:not(:last-child) {
    margin-right: 10px;
  }
`;

const PlanName = withStyles({
  root: {
    minWidth: 100,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
})(Typography);

const Header = () => {
  const {
    user,
    authSignOut,
    journeys,
    isSemanticsLoading,
    setIsShowHidden,
    configuration,
  } = useAppContext();
  const theme = useTheme();

  const history = useHistory();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const isCdpUser = isCdpOnlyUser(user);
  // Remove nudges and learning materials option for angelini users
  const tabs = (() => {
    const base =
      user.groups.includes("Admins") ||
      !user.companies?.includes(ANGELINI_COMPANY_ID)
        ? {
            0: "info",
            1: "structure-and-interactions",
            2: "scheduled-slots",
            3: "tips",
            4: "contextual",
            5: "learning-materials",
            6: "surveys",
          }
        : {
            0: "info",
            1: "structure-and-interactions",
            2: "scheduled-slots",
            3: "tips",
            // 4: "contextual",
            // 5: "learning-materials",
            4: "surveys",
          };

    if (!isCdpUser) {
      return base;
    }

    const withoutSurveys = Object.values(base).filter((t) => t !== "surveys");
    return Object.fromEntries(withoutSurveys.map((t, i) => [i, t]));
  })();

  const tabsInverted = invert(tabs);
  const settingsTabs = useMemo(() => {
    if (!user.groups.includes("Admins")) {
      return { 0: "integrations" };
    }

    const tabs = {
      0: "special-links",
      1: "cid-configurations",
      2: "calldetection-configuration",
      3: "companyuser-configuration",
      4: "integrations",
    };

    if (user.companies?.includes(DIGITALATTITUDE_COMPANY_ID)) {
      tabs[5] = "tip-groups-for-analytics";
    }

    return tabs;
  }, [user.groups, user.companies]);

  const welfaresTabs = useMemo(() => {
    if (
      user.groups.includes("Admins") ||
      user.companies?.includes(ANGELINI_COMPANY_ID)
    ) {
      const base = {
        0: "list",
        1: "news",
        2: "welfare-impersonate",
        3: "bookings",
      };

      if (
        user.companies?.includes(ANGELINI_COMPANY_ID) ||
        user.companies?.includes(DIGITALATTITUDE_COMPANY_ID)
      ) {
        return { ...base, 4: "employee-registry", 5: "hr-documents" };
      }

      // HR Documents shares the same access as the other Welfares tabs
      return { ...base, 4: "hr-documents" };
    }
    return {};
  }, [user.groups, user.companies]);
  const copilotTabs = user.groups.includes("Admins")
    ? {
        0: "prompt",
        1: "tools",
        2: "tags",
        3: "user-prompts",
      }
    : {};

  const newsTabs =
    user.groups.includes("Admins") ||
    user.companies?.includes(ALLITUDE_COMPANY_ID)
      ? {
          0: "news",
          1: "tags",
        }
      : {};

  const knowledgeHubTabs =
    user.groups.includes("Admins") ||
    user.companies?.includes(ENIKNOWLEDGEHUB_COMPANY_ID)
      ? {
          0: "prompt",
          1: "tools",
          2: "tags",
        }
      : {};

  const allitudeTabs =
    user.groups.includes("Admins") ||
    user.companies?.includes(ALLITUDE_COMPANY_ID)
      ? {
          0: "prompt",
          1: "tools",
          2: "tags",
        }
      : {};
  const aiSkillingTabs = user.groups.includes("Admins") //||
    ? //user.companies?.includes(ENIKNOWLEDGEHUB_COMPANY_ID)
      {
        0: "prompt",
        1: "tools",
        2: "tags",
      }
    : {};
  const fincantieriTabs = user.groups.includes("Admins")
    ? {
        0: "prompt",
        1: "tags",
      }
    : {};
  const cyberTipsCdpTabs =
    user.groups.includes("Admins") || user.companies?.includes(CDP_COMPANY_ID)
      ? {
          0: "prompt",
          1: "tags",
        }
      : {};

  const settingsTabsInverted = invert(settingsTabs);
  const welfaresTabsInverted = invert(welfaresTabs);
  const copilotTabsInverted = invert(copilotTabs);
  const newsTabsInverted = invert(newsTabs);
  const knowledgeHubTabsInverted = invert(knowledgeHubTabs);
  const allitudeTabsInverted = invert(allitudeTabs);
  const aiSkillingTabsInverted = invert(aiSkillingTabs);
  const fincantieriTabsInverted = invert(fincantieriTabs);
  const cyberTipsCdpTabsInverted = invert(cyberTipsCdpTabs);

  const journeyId = useMemo(() => {
    if (paths[1] === "journeys") {
      return paths[2];
    }

    return null;
  }, [paths]);

  const locale = useMemo(() => {
    if (paths[1] === "journeys") {
      return paths[3];
    }

    return null;
  }, [paths]);

  const journeyName = useMemo(() => {
    const journey = journeys.find((journey) => journey.id === journeyId);
    return journey?.internalName || journey?.name;
  }, [journeyId, journeys]);

  const [logoClickedCounter, setLogoClickedCounter] = useState(0);
  const goToMain = useCallback(() => {
    history.push("/");

    if (logoClickedCounter + 1 === 5) {
      setIsShowHidden((isShowHidden) => !isShowHidden);
      setLogoClickedCounter(0);
      return;
    }

    setLogoClickedCounter(logoClickedCounter + 1);

    if (logoClickedCounter === 0) {
      setTimeout(() => {
        setLogoClickedCounter(0);
      }, 5000);
    }
  }, [history, logoClickedCounter, setIsShowHidden]);

  const [tab, setTab] = useState(
    parseInt(
      (location.pathname.startsWith("/news")
        ? location.pathname.split("/")[2]
          ? newsTabsInverted[location.pathname.split("/")[2]?.toLowerCase()]
          : "0"
        : null) ??
        tabsInverted[location.pathname.split("/")[4]?.toLowerCase()] ??
        settingsTabsInverted[location.pathname.split("/")[2]?.toLowerCase()] ??
        welfaresTabsInverted[location.pathname.split("/")[2]?.toLowerCase()] ??
        copilotTabsInverted[location.pathname.split("/")[2]?.toLowerCase()] ??
        knowledgeHubTabsInverted[
          location.pathname.split("/")[2]?.toLowerCase()
        ] ??
        allitudeTabsInverted[location.pathname.split("/")[2]?.toLowerCase()] ??
        aiSkillingTabsInverted[
          location.pathname.split("/")[2]?.toLowerCase()
        ] ??
        fincantieriTabsInverted[
          location.pathname.split("/")[2]?.toLowerCase()
        ] ??
        cyberTipsCdpTabsInverted[
          location.pathname.split("/")[2]?.toLowerCase()
        ] ??
        "0",
      10
    )
  );

  useEffect(() => {
    setTab(
      parseInt(
        (location.pathname.startsWith("/news")
          ? location.pathname.split("/")[2]
            ? newsTabsInverted[location.pathname.split("/")[2]?.toLowerCase()]
            : "0"
          : null) ??
          tabsInverted[location.pathname.split("/")[4]?.toLowerCase()] ??
          settingsTabsInverted[
            location.pathname.split("/")[2]?.toLowerCase()
          ] ??
          welfaresTabsInverted[
            location.pathname.split("/")[2]?.toLowerCase()
          ] ??
          copilotTabsInverted[location.pathname.split("/")[2]?.toLowerCase()] ??
          knowledgeHubTabsInverted[
            location.pathname.split("/")[2]?.toLowerCase()
          ] ??
          allitudeTabsInverted[
            location.pathname.split("/")[2]?.toLowerCase()
          ] ??
          aiSkillingTabsInverted[
            location.pathname.split("/")[2]?.toLowerCase()
          ] ??
          fincantieriTabsInverted[
            location.pathname.split("/")[2]?.toLowerCase()
          ] ??
          cyberTipsCdpTabsInverted[
            location.pathname.split("/")[2]?.toLowerCase()
          ] ??
          "0",
        10
      )
    );
  }, [
    aiSkillingTabsInverted,
    fincantieriTabsInverted,
    cyberTipsCdpTabsInverted,
    allitudeTabsInverted,
    copilotTabsInverted,
    newsTabsInverted,
    knowledgeHubTabsInverted,
    location.pathname,
    settingsTabsInverted,
    tabsInverted,
    welfaresTabsInverted,
  ]);

  const { enqueueSnackbar } = useSnackbar();

  const handleTabChange = useCallback(
    (event, newTab) => {
      const paths = location.pathname.split("/");

      if (paths[1] === "settings") {
        setTab(newTab);
        history.replace(`/settings/${settingsTabs[newTab]}`, location.state);
        return;
      }

      if (paths[1] === "welfares") {
        setTab(newTab);
        history.replace(`/welfares/${welfaresTabs[newTab]}`, location.state);
        return;
      }

      if (paths[1] === "copilot") {
        setTab(newTab);
        history.replace(`/copilot/${copilotTabs[newTab]}`, location.state);
        return;
      }
      if (paths[1] === "news") {
        setTab(newTab);
        history.replace(`/news/${newsTabs[newTab]}`, location.state);
        return;
      }
      if (paths[1] === "knowledge-hub") {
        setTab(newTab);
        history.replace(
          `/knowledge-hub/${knowledgeHubTabs[newTab]}`,
          location.state
        );
        return;
      }
      if (paths[1] === "allitude") {
        setTab(newTab);
        history.replace(`/allitude/${allitudeTabs[newTab]}`, location.state);
        return;
      }
      if (paths[1] === "aiskilling") {
        setTab(newTab);
        history.replace(
          `/aiskilling/${aiSkillingTabs[newTab]}`,
          location.state
        );
        return;
      }
      if (paths[1] === "fincantieri") {
        setTab(newTab);
        history.replace(
          `/fincantieri/${fincantieriTabs[newTab]}`,
          location.state
        );
        return;
      }
      if (paths[1] === "cyber-tips-cdp") {
        setTab(newTab);
        history.replace(
          `/cyber-tips-cdp/${cyberTipsCdpTabs[newTab]}`,
          location.state
        );
        return;
      }

      if (paths[1] !== "journeys") {
        return;
      }

      if (newTab !== 0 && location.state?.isNew) {
        enqueueSnackbar("Save Journey first", { variant: "info" });
        return;
      }

      setTab(newTab);
      history.replace(
        `/journeys/${paths[2]}/${paths[3]}/${tabs[newTab]}`,
        location.state
      );
    },
    [
      location.pathname,
      location.state,
      history,
      //tabs,
      settingsTabs,
      welfaresTabs,
      copilotTabs,
      newsTabs,
      knowledgeHubTabs,
      allitudeTabs,
      aiSkillingTabs,
      fincantieriTabs,
      cyberTipsCdpTabs,
      enqueueSnackbar,
    ]
  );

  const handleBack = useCallback(() => {
    if (paths[1] === "image-galleries" && paths[2]) {
      history.push("/image-galleries");
      return;
    }
    if (paths[2] === "welcome-message") {
      history.push("/settings/cid-configurations");
      return;
    }

    if (paths[2] === "companyuser-configuration" && paths[3]) {
      history.push("/settings/companyuser-configuration");
      return;
    }

    if (paths[2] === "cid-configurations" && paths[3]) {
      history.push("/settings/cid-configurations");
      return;
    }

    if (paths[2] === "welcome-message") {
      history.push("/settings/cid-configurations");
      return;
    }

    if (
      ["learning-materials", "surveys"].includes(paths[4]?.toLowerCase()) &&
      paths[5]
    ) {
      // Remove learning-materials option for angelini users
      if (paths[4]?.toLowerCase() === "learning-materials") {
        if (
          user.groups.includes("Admins") ||
          !user.companies?.includes(ANGELINI_COMPANY_ID)
        ) {
          history.push(`/journeys/${paths[2]}/${paths[3]}/${paths[4]}`);
          return;
        }
        return;
      }
      history.push(`/journeys/${paths[2]}/${paths[3]}/${paths[4]}`);
      return;
    }

    if (["primes", "nudges"].includes(paths[4]?.toLowerCase())) {
      // Remove nudges option for angelini users

      if (paths[4]?.toLowerCase() === "nudges") {
        if (
          user.groups.includes("Admins") ||
          !user.companies?.includes(ANGELINI_COMPANY_ID)
        ) {
          history.push(
            `/journeys/${paths[2]}/${paths[3]}/${
              paths[4]?.toLowerCase() === "primes" ? tabs[3] : tabs[4]
            }`
          );
          return;
        } else {
          return;
        }
      }

      history.push(
        `/journeys/${paths[2]}/${paths[3]}/${
          paths[4]?.toLowerCase() === "primes" ? tabs[3] : tabs[4]
        }`
      );
      return;
    }

    if (paths[1] === "welfares") {
      if (paths[2] === "news" && paths[3]) {
        history.push("/welfares/news");
        return;
      }
      if (paths[2] === "employee-registry") {
        history.push("/welfares/employee-registry");
        return;
      }
      if (paths[2] === "hr-documents") {
        history.push("/welfares/hr-documents");
        return;
      }
      if (paths[2] && paths[2] !== "list") {
        history.push("/welfares");
        return;
      }
      history.push("/");
      return;
    }

    if (paths[1] === "copilot") {
      if (paths[2] === "prompt" && paths[3]) {
        history.push("/copilot/prompt");
        return;
      }
      if (paths[2] === "tools" && paths[3]) {
        history.push("/copilot/tools");
        return;
      }
      if (paths[2] === "tags" && paths[3]) {
        history.push("/copilot/tags");
        return;
      }
      if (paths[2] === "user-prompts" && paths[3]) {
        history.push("/copilot/user-prompts");
        return;
      }
      history.push("/");
      return;
    }
    if (paths[1] === "news") {
      if (paths[2] === "news" && paths[3]) {
        history.push("/news/news");
        return;
      }
      if (paths[2] === "tags" && paths[3]) {
        history.push("/news/tags");
        return;
      }
      history.push("/");
      return;
    }
    if (paths[1] === "knowledge-hub") {
      if (paths[2] === "prompt" && paths[3]) {
        history.push("/knowledge-hub/prompt");
        return;
      }
      if (paths[2] === "tools" && paths[3]) {
        history.push("/knowledge-hub/tools");
        return;
      }
      if (paths[2] === "tags" && paths[3]) {
        history.push("/knowledge-hub/tags");
        return;
      }
      history.push("/");
      return;
    }
    if (paths[1] === "allitude") {
      if (paths[2] === "prompt" && paths[3]) {
        history.push("/allitude/prompt");
        return;
      }
      if (paths[2] === "tools" && paths[3]) {
        history.push("/allitude/tools");
        return;
      }
      if (paths[2] === "tags" && paths[3]) {
        history.push("/allitude/tags");
        return;
      }
      history.push("/");
      return;
    }
    if (paths[1] === "aiskilling") {
      if (paths[2] === "prompt" && paths[3]) {
        history.push("/aiskilling/prompt");
        return;
      }
      if (paths[2] === "tools" && paths[3]) {
        history.push("/aiskilling/tools");
        return;
      }
      if (paths[2] === "tags" && paths[3]) {
        history.push("/aiskilling/tags");
        return;
      }
      history.push("/");
      return;
    }
    if (paths[1] === "fincantieri") {
      if (paths[2] === "prompt" && paths[3]) {
        history.push("/fincantieri/prompt");
        return;
      }
      if (paths[2] === "tags" && paths[3]) {
        history.push("/fincantieri/tags");
        return;
      }
      history.push("/");
      return;
    }
    if (paths[1] === "cyber-tips-cdp") {
      if (paths[2] === "prompt" && paths[3]) {
        history.push("/cyber-tips-cdp/prompt");
        return;
      }
      if (paths[2] === "tags" && paths[3]) {
        history.push("/cyber-tips-cdp/tags");
        return;
      }
      history.push("/");
      return;
    }

    history.push("/");
  }, [history, paths, user.companies, user.groups]);

  const getTabTitle = useCallback(
    ({ targetKeyword } = {}) => {
      const tab = targetKeyword;
      const basePath = location.pathname.split("/")[1];
      switch (tab) {
        case "prompt":
          switch (basePath) {
            case "allitude":
              return "Seeds";
            default:
              if (isCyberTipsSection(basePath)) {
                return getCyberTipsEntityLabel({ plural: true });
              }
              return "Prompts";
          }
        case "tools":
          switch (basePath) {
            case "allitude":
              return "Topics";
            default:
              return "Tools";
          }
        case "tags":
          return "Tags";
        case "user-prompts":
          return "User Prompts";
        default:
          return "";
      }
    },
    [location]
  );

  const isWelfareSection = useMemo(
    () =>
      /^(\/welfares(?:\/(news|list|employee-registry|hr-documents|welfare-impersonate|bookings))?\/?)*$/.test(
        location.pathname
      ),
    [location.pathname]
  );

  // Journeys show a second row of tabs under specific route conditions; mirror those here
  const showsJourneyTabs = useMemo(
    () =>
      location.pathname.startsWith("/journeys/") &&
      !location.pathname.includes("primes") &&
      !/learning-materials\/.+/.test(location.pathname) &&
      !/surveys\/.+/.test(location.pathname) &&
      !location.pathname.includes("nudges"),
    [location.pathname]
  );

  // Keep header background behavior minimal: solid only when a second-row of tabs is present

  return (
    <>
      <AppBar
        color={isWelfareSection || showsJourneyTabs ? "default" : "transparent"}
        elevation={1}
        position="fixed"
        style={{
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <ToolbarContainer>
                {location.pathname !== "/" ? (
                  <>
                    <Hidden mdDown>
                      <Button
                        disabled={
                          paths[4]?.toLowerCase() === "primes" &&
                          isSemanticsLoading
                        }
                        startIcon={
                          <ChevronLeftIcon color="action" fontSize="small" />
                        }
                        onClick={handleBack}
                      >
                        <Typography
                          color="textSecondary"
                          style={{ textTransform: "none" }}
                        >
                          Back
                        </Typography>
                      </Button>
                    </Hidden>
                    <Hidden lgUp>
                      <Tooltip title="Back">
                        <IconButton onClick={handleBack}>
                          <ChevronLeftIcon color="action" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Hidden>

                    {paths[1] === "settings" && !paths[3] && (
                      <>
                        <Divider flexItem light orientation="vertical" />
                        <Typography>Settings</Typography>
                      </>
                    )}

                    {paths[1] === "settings" &&
                      paths[2] === "cid-configurations" &&
                      paths[3] &&
                      configuration && (
                        <>
                          <Divider flexItem light orientation="vertical" />
                          <Typography>
                            {configuration.name} ({configuration.cid})
                          </Typography>
                        </>
                      )}

                    {journeyName && (
                      <>
                        <Divider flexItem light orientation="vertical" />
                        <Tooltip title={journeyName}>
                          <PlanName>{journeyName}</PlanName>
                        </Tooltip>
                      </>
                    )}
                  </>
                ) : (
                  <Link
                    style={{ color: "inherit", textDecoration: "none" }}
                    to="/"
                  >
                    <LogoContainer onClick={goToMain}>
                      <img alt="logo" src="/logo.png" width={24} />
                      <Typography variant="h6">
                        {process.env.REACT_APP_DASHBOARD_NAME} Portal
                      </Typography>
                    </LogoContainer>
                  </Link>
                )}

                {location.pathname.startsWith("/settings") ? (
                  <Box
                    display="flex"
                    flex={1}
                    justifyContent="center"
                    minWidth={0}
                    style={{ overflow: "hidden" }}
                  >
                    <SettingsTabs
                      indicatorColor="primary"
                      scrollButtons="on"
                      value={tab}
                      variant="scrollable"
                      onChange={handleTabChange}
                    >
                      {user.groups.includes("Admins")
                        ? [
                            <SettingsTab
                              key="special-link"
                              label="Special Link"
                            />,
                            <SettingsTab
                              key="cid-configuration"
                              label="CID config"
                            />,
                            <SettingsTab
                              key="calldetection-configuration"
                              label="Call detection"
                            />,
                            <SettingsTab
                              key="companyuser-configuration"
                              label="Partners access"
                            />,
                          ]
                        : []}
                      <SettingsTab label="Integrations" />
                      {user.groups.includes("Admins") &&
                      user.companies?.includes(
                        "5706867a-c563-4ca5-b803-c1883c38b640"
                      ) ? (
                        <SettingsTab
                          key="tip-groups-for-analytics"
                          label="Tip groups"
                        />
                      ) : (
                        []
                      )}
                      {/*user.groups.includes("Admins") && (
                        <SettingsTab
                          key="teams-gallery"
                          label="Teams Gallery"
                        />
                      )*/}
                    </SettingsTabs>
                  </Box>
                ) : location.pathname.match(
                    /^(\/welfares(?:\/(news|list|employee-registry|hr-documents|welfare-impersonate|bookings))?\/?)$/
                  ) ? (
                  <>
                    {(user.groups.includes("Admins") ||
                      user.companies?.includes(ANGELINI_COMPANY_ID)) && (
                      <Box display="flex" flex={1} justifyContent="center">
                        <WelfareSearch />
                      </Box>
                    )}
                  </>
                ) : location.pathname.startsWith("/copilot") ||
                  location.pathname.startsWith("/news") ||
                  location.pathname.startsWith("/knowledge-hub") ||
                  location.pathname.startsWith("/allitude") ||
                  location.pathname.startsWith("/aiskilling") ||
                  location.pathname.startsWith("/fincantieri") ||
                  location.pathname.startsWith("/cyber-tips-cdp") ? (
                  <Box display="flex" flex={1} justifyContent="center">
                    <Tabs
                      centered
                      indicatorColor="primary"
                      value={tab}
                      variant="fullWidth"
                      onChange={handleTabChange}
                    >
                      {(user.groups.includes("Admins") ||
                        user.companies?.includes(ALLITUDE_COMPANY_ID)) &&
                      location.pathname.startsWith("/news")
                        ? [
                            <Tab key="news" wrapped label="News" />,
                            <Tab key="tags" wrapped label="Tags" />,
                          ]
                        : location.pathname.startsWith("/fincantieri") &&
                          user.groups.includes("Admins")
                        ? [
                            <Tab
                              key="prompt"
                              wrapped
                              label={getTabTitle({ targetKeyword: "prompt" })}
                            />,
                            <Tab key="tags" wrapped label="Tags" />,
                          ]
                        : location.pathname.startsWith("/cyber-tips-cdp") &&
                          (user.groups.includes("Admins") ||
                            user.companies?.includes(CDP_COMPANY_ID))
                        ? [
                            <Tab
                              key="prompt"
                              wrapped
                              label={getTabTitle({ targetKeyword: "prompt" })}
                            />,
                            <Tab key="tags" wrapped label="Tags" />,
                          ]
                        : user.groups.includes("Admins") ||
                          (user.companies?.includes(
                            ENIKNOWLEDGEHUB_COMPANY_ID
                          ) &&
                            location.pathname.startsWith("/knowledge-hub")) ||
                          (location.pathname.startsWith("/allitude") &&
                            user.companies?.includes(ALLITUDE_COMPANY_ID))
                        ? [
                            <Tab
                              key="prompt"
                              wrapped
                              label={getTabTitle({ targetKeyword: "prompt" })}
                            />,
                            <Tab
                              key="tools"
                              wrapped
                              label={getTabTitle({ targetKeyword: "tools" })}
                            />,
                            <Tab key="tags" wrapped label="Tags" />,
                            ...(location.pathname.startsWith("/copilot")
                              ? [
                                  <Tab
                                    key="user-prompts"
                                    wrapped
                                    label={getTabTitle({
                                      targetKeyword: "user-prompts",
                                    })}
                                  />,
                                ]
                              : []),
                          ]
                        : []}
                    </Tabs>
                  </Box>
                ) : (
                  <Box display="flex" flex={1} />
                )}

                {journeyId && (
                  <Box display="flex" flex={1} justifyContent="center">
                    <SemanticsSearch journeyId={journeyId} locale={locale} />
                  </Box>
                )}
                {(user.companies?.includes(ALLITUDE_COMPANY_ID) ||
                  (user.groups.includes("Admins") &&
                    user.companies?.includes(
                      "5706867a-c563-4ca5-b803-c1883c38b640"
                    ))) && (
                  <Tooltip title="News">
                    <Link
                      style={{ color: "inherit", textDecoration: "none" }}
                      to="/news/news"
                    >
                      <IconButton size="small">
                        <MenuBookIcon />
                      </IconButton>
                    </Link>
                  </Tooltip>
                )}
                {/* AI Skilling Section */}
                {
                  // Anybody can access this; content is filtered inside
                  <Tooltip title="Users' lists">
                    <Link
                      style={{ color: "inherit", textDecoration: "none" }}
                      to="/userslists"
                    >
                      <IconButton size="small">
                        <Group />
                      </IconButton>
                    </Link>
                  </Tooltip>
                }
                {/* AI Skilling Section */}
                {user.groups.includes("Admins") &&
                  user.companies?.includes(
                    "5706867a-c563-4ca5-b803-c1883c38b640"
                  ) && ( //||
                    //user.companies?.includes(ALLITUDE_COMPANY_ID)) && (
                    <Tooltip title="AI Skilling">
                      <Link
                        style={{ color: "inherit", textDecoration: "none" }}
                        to="/aiskilling/prompt"
                      >
                        <IconButton size="small">
                          <ForumIcon />
                        </IconButton>
                      </Link>
                    </Tooltip>
                  )}
                {/* Allitude Section */}
                {((user.groups.includes("Admins") &&
                  user.companies?.includes(
                    "5706867a-c563-4ca5-b803-c1883c38b640"
                  )) ||
                  user.companies?.includes(ALLITUDE_COMPANY_ID)) && (
                  <Tooltip title="Seeds di Conoscenza">
                    <Link
                      style={{ color: "inherit", textDecoration: "none" }}
                      to="/allitude/prompt"
                    >
                      <IconButton size="small">
                        <CastForEducationIcon />
                      </IconButton>
                    </Link>
                  </Tooltip>
                )}
                {/* KnowledgeHub section is just for D.A. admins, or EniKnowledgeHub */}
                {((user.groups.includes("Admins") &&
                  user.companies?.includes(
                    "5706867a-c563-4ca5-b803-c1883c38b640"
                  )) ||
                  user.companies?.includes(ENIKNOWLEDGEHUB_COMPANY_ID)) && (
                  <Tooltip title="Knowledge Hub">
                    <Link
                      style={{ color: "inherit", textDecoration: "none" }}
                      to="/knowledge-hub/prompt"
                    >
                      <IconButton size="small">
                        <DeviceHubIcon />
                      </IconButton>
                    </Link>
                  </Tooltip>
                )}
                {user.groups.includes("Admins") && (
                  <Tooltip title="Cyber Tips">
                    <Link
                      style={{ color: "inherit", textDecoration: "none" }}
                      to="/fincantieri/prompt"
                    >
                      <IconButton size="small">
                        <SecurityIcon />
                      </IconButton>
                    </Link>
                  </Tooltip>
                )}
                {(user.groups.includes("Admins") ||
                  user.companies?.includes(CDP_COMPANY_ID)) && (
                  <Tooltip title="Cyber Tips (CDP)">
                    <Link
                      style={{ color: "inherit", textDecoration: "none" }}
                      to="/cyber-tips-cdp/prompt"
                    >
                      <IconButton size="small">
                        <VerifiedUserIcon />
                      </IconButton>
                    </Link>
                  </Tooltip>
                )}
                {/* Prompt section is just for D.A. admins */}
                {user.groups.includes("Admins") &&
                  user.companies?.includes(
                    "5706867a-c563-4ca5-b803-c1883c38b640"
                  ) && (
                    <Tooltip title="Copilot">
                      <Link
                        style={{ color: "inherit", textDecoration: "none" }}
                        to="/copilot/prompt"
                      >
                        <IconButton size="small">
                          <EmojiObjectsIcon />
                        </IconButton>
                      </Link>
                    </Tooltip>
                  )}

                {(user.groups.includes("Admins") ||
                  user.companies?.includes(ANGELINI_COMPANY_ID)) && (
                  <Tooltip title="Welfares">
                    <Link
                      style={{ color: "inherit", textDecoration: "none" }}
                      to="/welfares"
                    >
                      <IconButton size="small">
                        <SpaIcon />
                      </IconButton>
                    </Link>
                  </Tooltip>
                )}
                <Tooltip title="User's metadata">
                  <Link
                    style={{ color: "inherit", textDecoration: "none" }}
                    to="/user-metadata"
                  >
                    <IconButton size="small">
                      <CodeIcon />
                    </IconButton>
                  </Link>
                </Tooltip>
                <Tooltip title="Simulation">
                  <Link
                    style={{ color: "inherit", textDecoration: "none" }}
                    to="/simulation"
                  >
                    <IconButton size="small">
                      <DevicesIcon />
                    </IconButton>
                  </Link>
                </Tooltip>
                <Tooltip title="Image Galleries">
                  <Link
                    style={{ color: "inherit", textDecoration: "none" }}
                    to="/image-galleries"
                  >
                    <IconButton size="small">
                      <PermMediaIcon />
                    </IconButton>
                  </Link>
                </Tooltip>
                {user.groups.includes("Admins") && (
                  <Tooltip title="Setting">
                    <Link
                      style={{ color: "inherit", textDecoration: "none" }}
                      to="/settings"
                    >
                      <IconButton size="small">
                        <SettingsIcon />
                      </IconButton>
                    </Link>
                  </Tooltip>
                )}
                <Divider flexItem light orientation="vertical" />
                <Hidden mdDown>
                  <Box>
                    <Typography>
                      {user.given_name} {user.family_name}
                    </Typography>
                  </Box>
                  <ProfileAvatar>
                    {user.given_name?.charAt(0).toUpperCase()}
                    {user.family_name?.charAt(0).toUpperCase()}
                  </ProfileAvatar>
                </Hidden>
                <Hidden lgUp>
                  <Tooltip title={`${user.given_name} ${user.family_name}`}>
                    <ProfileAvatar>
                      {user.given_name?.charAt(0).toUpperCase()}
                      {user.family_name?.charAt(0).toUpperCase()}
                    </ProfileAvatar>
                  </Tooltip>
                </Hidden>
                <Divider flexItem light orientation="vertical" />
                <Hidden mdDown>
                  <SignOutButton
                    color="inherit"
                    endIcon={<SignOutIcon fontSize="small" />}
                    fontSize="small"
                    onClick={authSignOut}
                  >
                    Sign out
                  </SignOutButton>
                </Hidden>
                <Hidden lgUp>
                  <Tooltip title="Sign Out">
                    <IconButton color="inherit" onClick={authSignOut}>
                      <SignOutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Hidden>
              </ToolbarContainer>
            </Grid>
            {location.pathname.match(
              /^(\/welfares(?:\/(news|list|employee-registry|hr-documents|welfare-impersonate|bookings))?\/?)*$/
            ) && (
              <Grid item>
                <Box display="flex" flex={1} justifyContent="center">
                  <Tabs
                    centered
                    indicatorColor="primary"
                    value={tab}
                    variant="fullWidth"
                    onChange={handleTabChange}
                  >
                    {user.groups.includes("Admins") ||
                    user.companies?.includes(ANGELINI_COMPANY_ID)
                      ? [
                          <Tab key="list" wrapped label="Welfares" />,
                          <Tab key="news" wrapped label="News" />,
                          <Tab
                            key="welfare-impersonate"
                            wrapped
                            label="Simulate"
                          />,
                          <Tab key="bookings" wrapped label="Bookings" />,
                          // Show Employee Registry tab only for Angelini or DigitalAttitude users
                          ...(user.companies?.includes(ANGELINI_COMPANY_ID) ||
                          user.companies?.includes(DIGITALATTITUDE_COMPANY_ID)
                            ? [
                                <Tab
                                  key="employee-registry"
                                  wrapped
                                  label="Employee Registry"
                                />,
                              ]
                            : []),
                          // Same access as the other Welfares tabs
                          <Tab
                            key="hr-documents"
                            wrapped
                            label="HR Documents"
                          />,
                        ]
                      : []}
                  </Tabs>
                </Box>
              </Grid>
            )}

            {location.pathname.startsWith("/journeys/") &&
              !location.pathname.includes("primes") &&
              !location.pathname.match(/learning-materials\/.+/) &&
              !location.pathname.match(/surveys\/.+/) &&
              !location.pathname.includes("nudges") && (
                <Grid item>
                  <Box display="flex" flex={1} justifyContent="center">
                    {user.companies?.includes(SNAM_COMPANY_ID) &&
                    !user.groups.includes("Admins") ? (
                      <Tabs
                        centered
                        indicatorColor="primary"
                        value={tab}
                        variant="fullWidth"
                        onChange={handleTabChange}
                      >
                        <Tab wrapped label="General Info" />
                        <Tab wrapped label="Structure" />
                        <Tab wrapped label="Scheduled Slots" />
                        <Tab wrapped label="Tips" />
                        {/* <Tab wrapped label="Nudges" /> */}
                        {/* <Tab wrapped label="Learning Materials" /> */}
                        {/* <Tab wrapped label="Tests/Surveys" /> */}
                      </Tabs>
                    ) : user.groups.includes("Admins") ||
                      !user.companies?.includes(ANGELINI_COMPANY_ID) ? (
                      <Tabs
                        centered
                        indicatorColor="primary"
                        value={tab}
                        variant="fullWidth"
                        onChange={handleTabChange}
                      >
                        <Tab wrapped label="General Info" />
                        <Tab wrapped label="Structure" />
                        <Tab wrapped label="Scheduled Slots" />
                        <Tab wrapped label="Tips" />
                        <Tab wrapped label="Nudges" />
                        <Tab wrapped label="Learning Materials" />
                        {!isCdpUser && <Tab wrapped label="Tests/Surveys" />}
                      </Tabs>
                    ) : (
                      <Tabs
                        centered
                        indicatorColor="primary"
                        value={tab}
                        variant="fullWidth"
                        onChange={handleTabChange}
                      >
                        <Tab wrapped label="General Info" />
                        <Tab wrapped label="Structure" />
                        <Tab wrapped label="Scheduled Slots" />
                        <Tab wrapped label="Tips" />
                        {/* <Tab wrapped label="Nudges" /> */}
                        {/* <Tab wrapped label="Learning Materials" /> */}
                        {!isCdpUser && <Tab wrapped label="Tests/Surveys" />}
                      </Tabs>
                    )}
                  </Box>
                </Grid>
              )}
          </Grid>
        </Toolbar>
      </AppBar>
      {/* No extra spacer here; content offset handled in MainApp */}
    </>
  );
};

export default Header;
